import Component from '@glimmer/component';
import { type EventInfoComponentArgs } from 'donor-elf-web/components/event/index';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import dateUtils from 'donor-elf-web/lib/date-utils';
import LeftBorderCard from 'donor-elf-web/components/left-border-card';
import BasicDropdown from 'donor-elf-web/components/basic-dropdown';
import DropdownWithFooter from 'donor-elf-web/components/dropdown-with-footer';
import FormUnderlineLabel from 'donor-elf-web/components/form/underline/label';
import GenericError from 'donor-elf-web/components/generic-error';
import formatDate from 'donor-elf-web/helpers/format-date';
import closeDropdown from 'donor-elf-web/helpers/close-dropdown';
import isPresentHelper from 'donor-elf-web/helpers/is-present';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { fn } from '@ember/helper';

interface PhoneEmailCustomInfo {
  l?: string;
  v: any;
  revert?: boolean;
  reverted_on?: string;
}

class PhoneEmailChange {
  constructor(label: string, info: PhoneEmailCustomInfo) {
    this.label = label;
    this.info = info;
  }

  label: string;
  info: PhoneEmailCustomInfo;
  @tracked revertingChange = false;
  @tracked error: any;

  get canRevertChange() {
    return (
      this.info.reverted_on == null &&
      !this.revertingChange &&
      this.error == null
    );
  }
}

let nameFieldLabels: any = {
  name: 'Name',
  mailing_name: 'Mailing Name',
  greeting: 'Greeting',
  first_name: 'First Name',
  last_name: 'Last Name',
  spouse_first_name: 'Spouse First Name',
  spouse_last_name: 'Spouse Last Name',
};

export default class EventInfoChangedComponent extends Component<EventInfoComponentArgs> {
  @service declare contactsCache: any;

  @tracked revertingChange = false;

  get addressChanged() {
    return this.args.eventModel.custom.old_address != null;
  }

  get addressChangeRevertedOn(): Date | undefined {
    let revertedDate =
      this.args.eventModel.custom['address_change_reverted_on'];
    if (revertedDate != null) {
      return dateUtils.deserializeDateOnly(revertedDate);
    } else {
      return undefined;
    }
  }

  get oldAddressCityAndState() {
    let { city, state } = this.args.eventModel.custom.old_address;
    return [city, state].filter((x) => isPresent(x)).join(', ');
  }

  get newAddressCityAndState() {
    let { city, state } = this.args.eventModel.custom.new_address;
    return [city, state].filter((x) => isPresent(x)).join(', ');
  }

  get nameChanges() {
    let { custom } = this.args.eventModel;
    return Object.keys(custom.changes)
      .filter((x) => nameFieldLabels[x] != null)
      .map((field) => {
        return {
          label: nameFieldLabels[field],
          old: custom.changes[field][0],
          new: custom.changes[field][1],
        };
      });
  }

  get nameChangesRevertedOn(): Date | undefined {
    let revertedDate = this.args.eventModel.custom['name_changes_reverted_on'];
    if (revertedDate != null) {
      return dateUtils.deserializeDateOnly(revertedDate);
    } else {
      return undefined;
    }
  }

  get namesOrAddressChanged() {
    return this.addressChanged || isPresent(this.nameChanges);
  }

  get changedPhonesAndEmails(): PhoneEmailChange[] {
    let { custom } = this.args.eventModel;
    return [
      ...(custom.added_phones || []).map((x: PhoneEmailCustomInfo) => {
        return new PhoneEmailChange('Added Phone', x);
      }),
      ...(custom.deleted_phones || []).map((x: PhoneEmailCustomInfo) => {
        return new PhoneEmailChange('Deleted Phone', x);
      }),
      ...(custom.added_emails || []).map((x: PhoneEmailCustomInfo) => {
        return new PhoneEmailChange('Added Email', x);
      }),
      ...(custom.deleted_emails || []).map((x: PhoneEmailCustomInfo) => {
        return new PhoneEmailChange('Deleted Email', x);
      }),
    ];
  }

  handleRevertAddressChange = async () => {
    this.revertingChange = true;
    let { eventModel } = this.args;
    eventModel.custom['update_action'] = { name: 'revert_address_change' };
    await eventModel.save();
    await this.contactsCache.reloadContact(eventModel.contactId);
    this.revertingChange = false;
  }

  handleRevertNameChanges = async () => {
    this.revertingChange = true;
    let { eventModel } = this.args;
    eventModel.custom['update_action'] = { name: 'revert_name_changes' };
    await eventModel.save();
    await this.contactsCache.reloadContact(eventModel.contactId);
    this.revertingChange = false;
  }

  handleRevertPhoneOrEmailChange = async (item: PhoneEmailChange) => {
    item.revertingChange = true;
    item.info.revert = true;
    let { eventModel } = this.args;
    eventModel.custom['update_action'] = {
      name: 'revert_phone_or_email_change',
    };
    try {
      await eventModel.save();
      await this.contactsCache.reloadContact(eventModel.contactId);
    } catch (error) {
      item.error = error;
    }
    item.revertingChange = false;
  }

  <template>
    <div>
      <p class='font-weight-light tw-text-sm'>
        The new field values below came from a change in the organization's
        accounting system.
      </p>
      {{#if this.addressChanged}}
        <table class='table table-sm tw-text-sm'>
          <thead>
            <tr>
              <th>Old Address</th>
              <th>New Address</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div>{{@eventModel.custom.old_address.address1}}</div>
                <div>{{@eventModel.custom.old_address.address2}}</div>
                <div>
                  {{this.oldAddressCityAndState}}
                  {{@eventModel.custom.old_address.zip}}
                </div>
                <div>
                  {{#if this.addressChangeRevertedOn}}
                    <LeftBorderCard class='mt-2 tw-shadow-md tw-max-w-48'>
                      <div class='p-1'>
                        Address was changed back to this one on
                        {{formatDate this.addressChangeRevertedOn}}
                      </div>
                    </LeftBorderCard>
                  {{else}}
                    {{#if this.revertingChange}}
                      <LeftBorderCard class='mt-2 tw-shadow-md tw-max-w-48'>
                        <div class='p-1'>
                          Undoing the change...
                        </div>
                      </LeftBorderCard>
                    {{else}}
                      <BasicDropdown as |dd|>
                        <dd.Trigger>
                          <button
                            class='btn btn-sm btn-outline-brand py-0 mt-1'
                            type='button'
                          >
                            Undo Change
                          </button>
                        </dd.Trigger>
                        <dd.Content
                          class='de-dropdown-content mt-2 tw-max-w-64 tw-text-sm'
                        >
                          <DropdownWithFooter
                            @onYes={{this.handleRevertAddressChange}}
                            @onNo={{closeDropdown dd}}
                          >
                            <div>
                              Are you sure you want to change the address back to:
                            </div>
                            <div class='font-weight-light text-muted my-2'>
                              <div>{{@eventModel.custom.old_address.address1}}</div>
                              <div>{{@eventModel.custom.old_address.address2}}</div>
                              <div>
                                {{this.oldAddressCityAndState}}
                                {{@eventModel.custom.old_address.zip}}
                              </div>
                            </div>
                          </DropdownWithFooter>
                        </dd.Content>
                      </BasicDropdown>
                    {{/if}}
                  {{/if}}
                </div>
              </td>
              <td>
                <div>{{@eventModel.custom.new_address.address1}}</div>
                <div>{{@eventModel.custom.new_address.address2}}</div>
                <div>
                  {{this.newAddressCityAndState}}
                  {{@eventModel.custom.new_address.zip}}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      {{/if}}
      {{#let this.nameChanges as |nameChanges|}}
        {{#if (isPresentHelper nameChanges)}}
          <div class='mb-2'>
            <table class='table table-sm tw-text-sm mb-0'>
              <thead>
                <tr>
                  <th>Name Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {{#each nameChanges as |change|}}
                  <tr>
                    <td>{{change.label}}</td>
                    <td>{{change.old}}</td>
                    <td>{{change.new}}</td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
            {{#if this.nameChangesRevertedOn}}
              <LeftBorderCard class='mt-2 tw-text-sm tw-shadow-md tw-max-w-96'>
                <div class='p-1'>
                  Names were changed back to the old values on
                  {{formatDate this.nameChangesRevertedOn}}
                </div>
              </LeftBorderCard>
            {{else}}
              {{#if this.revertingChange}}
                <LeftBorderCard class='mt-2 tw-text-sm tw-shadow-md'>
                  <div class='p-1'>
                    Undoing the change...
                  </div>
                </LeftBorderCard>
              {{else}}
                <BasicDropdown as |dd|>
                  <dd.Trigger>
                    <button class='btn btn-sm btn-outline-brand py-0' type='button'>
                      Undo All Name Changes
                    </button>
                  </dd.Trigger>
                  <dd.Content
                    class='de-dropdown-content mt-2 tw-max-w-64 tw-text-sm'
                  >
                    <DropdownWithFooter
                      @onYes={{this.handleRevertNameChanges}}
                      @onNo={{closeDropdown dd}}
                    >
                      <div>
                        Are you sure you want to change the name fields back to:
                      </div>
                      <div class='my-2'>
                        {{#each this.nameChanges as |nameChange|}}
                          <FormUnderlineLabel
                            @label={{nameChange.label}}
                            class='mb-2'
                          >
                            {{#if (isPresentHelper nameChange.old)}}
                              {{nameChange.old}}
                            {{else}}
                              {blank value}
                            {{/if}}
                          </FormUnderlineLabel>
                        {{/each}}
                      </div>
                    </DropdownWithFooter>
                  </dd.Content>
                </BasicDropdown>
              {{/if}}
            {{/if}}
          </div>
        {{/if}}
      {{/let}}
      {{#if (isPresentHelper this.changedPhonesAndEmails)}}
        <table
          class='table table-sm tw-text-sm {{if this.namesOrAddressChanged "mt-4"}}'
        >
          <thead>
            <tr>
              <th></th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {{#each this.changedPhonesAndEmails as |item|}}
              <tr>
                <td>{{item.label}}</td>
                <td>
                  <div class='d-flex'>
                    <div class='flex-grow-1'>
                      {{item.info.v}}
                      {{#if (isPresentHelper item.info.l)}}
                        <span class='font-weight-light text-muted'>
                          ({{item.info.l}})
                        </span>
                      {{/if}}
                    </div>
                    {{#if item.canRevertChange}}
                      <div class='ml-2'>
                        <BasicDropdown @horizontalPosition='auto-right' as |dd|>
                          <dd.Trigger>
                            <div class='icon-link cursor-pointer' role='button'>
                              {{svgJar 'undo-solid'}}
                            </div>
                          </dd.Trigger>
                          <dd.Content
                            class='de-dropdown-content mt-2 tw-max-w-64 tw-text-sm'
                          >
                            <DropdownWithFooter
                              @onYes={{fn this.handleRevertPhoneOrEmailChange item}}
                              @onNo={{closeDropdown dd}}
                            >
                              Are you sure you want to undo this change?
                            </DropdownWithFooter>
                          </dd.Content>
                        </BasicDropdown>
                      </div>
                    {{/if}}
                  </div>
                  {{#if item.info.reverted_on}}
                    <div class='d-flex'>
                      <LeftBorderCard class='mt-2 tw-text-sm tw-shadow-md'>
                        <div class='p-1'>
                          This change was undone on
                          {{formatDate item.info.reverted_on}}
                        </div>
                      </LeftBorderCard>
                    </div>
                  {{else if item.revertingChange}}
                    <div class='d-flex'>
                      <LeftBorderCard class='mt-2 tw-text-sm tw-shadow-md'>
                        <div class='p-1'>
                          Undoing the change...
                        </div>
                      </LeftBorderCard>
                    </div>
                  {{else if item.error}}
                    <div class='d-flex'>
                      <GenericError
                        @error={{item.error}}
                        @useLeftBorderCard={{true}}
                        class='mt-2 tw-text-sm tw-shadow-md'
                      />
                    </div>
                  {{/if}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/if}}
    </div>
  </template>
}
